import { Image, Pressable, StyleSheet, Text as RNText, View } from 'react-native';

import { colors, fonts, fontSizes, radii, shadows, spacing } from '@/constants/theme';
import type { GiftProduct } from '@/types/gifting';

/**
 * One tile in the 2-col gift grid. Sold-out tiles stay visible (dimmed +
 * pill) but aren't pressable — vanishing products read as a broken grid.
 */
export function GiftProductCard({
  product,
  onPress,
}: {
  product: GiftProduct;
  onPress: (product: GiftProduct) => void;
}) {
  const soldOut = product.sold_out;

  return (
    <Pressable
      style={({ pressed }) => [styles.card, pressed && !soldOut && styles.cardPressed]}
      onPress={() => onPress(product)}
      disabled={soldOut}
      accessibilityRole="button"
      accessibilityLabel={soldOut ? `${product.name}, sold out` : `View ${product.name}`}
      accessibilityState={{ disabled: soldOut }}
      testID={`gift-product-${product.slug}`}
    >
      <View style={[styles.imageWrap, soldOut && styles.dimmed]}>
        {product.image_url ? (
          <Image source={{ uri: product.image_url }} style={styles.image} resizeMode="cover" />
        ) : (
          <View style={styles.imageFallback}>
            <RNText style={styles.imageFallbackText}>{product.name.slice(0, 1)}</RNText>
          </View>
        )}
        {soldOut && (
          <View style={styles.soldOutPill}>
            <RNText style={styles.soldOutText}>SOLD OUT</RNText>
          </View>
        )}
      </View>
      <View style={[styles.meta, soldOut && styles.dimmed]}>
        <RNText style={styles.name} numberOfLines={1}>
          {product.name}
        </RNText>
        {/* Brand, not category: "this is your introduction to the brand" —
            an internal taxonomy word tells the athlete nothing. */}
        <RNText style={styles.category} numberOfLines={1}>
          {product.brand_name}
        </RNText>
      </View>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  card: {
    flex: 1,
    backgroundColor: colors.surface,
    borderRadius: radii.lg,
    borderWidth: 1,
    borderColor: colors.border,
    overflow: 'hidden',
    ...shadows.card,
  },
  cardPressed: {
    opacity: 0.85,
  },
  imageWrap: {
    aspectRatio: 1,
    backgroundColor: colors.background,
  },
  image: {
    width: '100%',
    height: '100%',
  },
  imageFallback: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.brightGreenSoft,
  },
  imageFallbackText: {
    fontFamily: fonts.display,
    fontSize: fontSizes['5xl'],
    color: colors.deepGreen,
  },
  dimmed: {
    opacity: 0.45,
  },
  soldOutPill: {
    position: 'absolute',
    top: spacing.sm,
    left: spacing.sm,
    backgroundColor: colors.charcoal,
    borderRadius: radii.full,
    paddingHorizontal: spacing.md - 1,
    paddingVertical: 5,
    opacity: 1,
  },
  soldOutText: {
    fontFamily: fonts.bodyBold,
    fontSize: fontSizes.xxs,
    color: colors.frost,
    letterSpacing: 0.5,
  },
  meta: {
    padding: spacing.md,
  },
  name: {
    fontFamily: fonts.bodySemiBold,
    fontSize: fontSizes.sm,
    color: colors.text,
  },
  category: {
    fontFamily: fonts.body,
    fontSize: fontSizes.xs,
    color: colors.textMuted,
    marginTop: 2,
  },
});
